import { useMemo, useRef, useState } from "react";
import { useGLTF } from "@react-three/drei";
import * as THREE from "three";
import { useFrame, type ThreeEvent } from "@react-three/fiber";
import {
  buildLoads, chunkByGrid, chunkCenter, lodFar, BOX_MODEL, PALLET_MODEL, INFERRED_COLOR,
  type BoxPlacement, type PalletPlacement, type PickVolume, boxAddress,
} from "./palletLoad";
import type { OccupancyCell } from "./OccupancyLayer";
import { StaticInstances } from "./StaticInstances";
import { hoverAuraMaterial, searchAuraMaterial, selectedAuraMaterial } from "./auraMaterial";
import { useUIStore } from "../stores/uiStore";
import { useTelemetryStore } from "../stores/telemetryStore";
import { tokens } from "../styles/tokens";

/** Real parts sliced out of warehouse_UL.glb (ADR 191) — meters, Y-up, origin at
 *  the base center, so an instance's position IS where its base sits. */
const PALLET_URL = "/models/pallet_medium.glb";
const BOX_URL = "/models/box_carton.glb";
// Warm the cache at module load — but not under vitest, where JSDOM has no
// base URL to resolve the fetch against (Forklift's preloads never enter a
// test import graph; this module does, via OccupancyLayerContainer).
if (!import.meta.env.TEST) {
  useGLTF.preload(PALLET_URL);
  useGLTF.preload(BOX_URL);
}

/** Same lift as OccupancyLayer's floor group, so a floor-level pallet sits on
 *  the deck tint rather than z-fighting the warehouse floor. */
const FLOOR_Y = 0.03;

/** Natural = the GLB's own material untinted (instance color multiplies). */
const NATURAL = "#ffffff";

/** Non-matching loads under an active search (ADR 196): same geometry, one
 *  cloned near-transparent material. depthWrite off keeps the ghosts from
 *  occluding each other into a solid wall. */
const GHOST_OPACITY = 0.12;

/** Far-LOD proxies (ADR 199): 12-tri boxes with the measured part dims and the
 *  same base-origin frame, so the existing instance matrices apply unchanged.
 *  The GLB materials stay on — only the silhouette simplifies. */
const PALLET_PROXY = new THREE.BoxGeometry(PALLET_MODEL.length, PALLET_MODEL.height, PALLET_MODEL.width);
PALLET_PROXY.translate(0, PALLET_MODEL.height / 2, 0);
const BOX_PROXY = new THREE.BoxGeometry(BOX_MODEL.footprint, BOX_MODEL.height, BOX_MODEL.footprint);
BOX_PROXY.translate(0, BOX_MODEL.height / 2, 0);

/** Check the camera's LOD distances every N frames — the swap has hysteresis
 *  (lodFar), so a coarse cadence costs nothing visible. */
const LOD_CHECK_EVERY = 15;

/** LOD distances measure to a chunk center at mid-rack height, not the floor —
 *  a top-down camera should read as farther than an in-aisle one. */
const CHUNK_MID_Y = 3;

function firstMesh(root: THREE.Object3D): THREE.Mesh | null {
  let found: THREE.Mesh | null = null;
  root.traverse((o) => { if (!found && (o as THREE.Mesh).isMesh) found = o as THREE.Mesh; });
  return found;
}

/** A material that shows the instance colour as-is: the base colour map dropped
 *  and the base white, so tint × base = tint (ADR 393). */
function neutralOf(m: THREE.Material | THREE.Material[]): THREE.Material | THREE.Material[] {
  const one = (x: THREE.Material) => {
    const c = x.clone() as THREE.MeshStandardMaterial;
    if ("map" in c) c.map = null;
    if ("color" in c && c.color) c.color.set("#ffffff");
    c.needsUpdate = true;
    return c;
  };
  return Array.isArray(m) ? m.map(one) : one(m);
}

function ghostOf(material: THREE.Material | THREE.Material[]): THREE.Material {
  const base = Array.isArray(material) ? material[0] : material;
  const m = base.clone();
  m.transparent = true;
  m.opacity = GHOST_OPACITY;
  m.depthWrite = false;
  return m;
}

const _pos = new THREE.Vector3();
const _quat = new THREE.Quaternion();
const _scl = new THREE.Vector3();
const _yAxis = new THREE.Vector3(0, 1, 0);

function composePallet(p: PalletPlacement, out: THREE.Matrix4) {
  _quat.setFromAxisAngle(_yAxis, p.rotY);
  out.compose(_pos.set(p.x, p.elev, p.y), _quat, _scl.set(1, 1, 1));
}

function composeBox(b: BoxPlacement, out: THREE.Matrix4) {
  _quat.setFromAxisAngle(_yAxis, b.rotY);
  out.compose(_pos.set(b.x, b.elev, b.y), _quat, _scl.set(b.scale[0], b.scale[1], b.scale[2]));
}

/** The shared pick geometry is a centred unit cube — the instance matrix
 *  stretches it to the bin bbox and lifts it to the load's mid-height. */
function composePick(p: PickVolume, out: THREE.Matrix4) {
  _quat.identity();
  out.compose(_pos.set(p.x, p.elev + p.h / 2, p.y), _quat, _scl.set(p.w, p.h, p.d));
}

/** Aura shells (ADR 202) reuse the pick bbox, inflated so the glow floats
 *  just off the load instead of clipping its faces. */
const AURA_PAD = 0.12;
const AURA_GEOMETRY = new THREE.BoxGeometry(1, 1, 1);
function composeAura(p: PickVolume, out: THREE.Matrix4) {
  _quat.identity();
  out.compose(
    _pos.set(p.x, p.elev + p.h / 2, p.y),
    _quat,
    _scl.set(p.w + AURA_PAD * 2, p.h + AURA_PAD * 2, p.d + AURA_PAD * 2),
  );
}

const boxColor = (b: BoxPlacement) => (b.danger ? tokens.semantic.danger : NATURAL);
const inferredColor = () => INFERRED_COLOR;

interface Props {
  locations: OccupancyCell[];
  /** ADR 196 product search — same contract as OccupancyLayer's prop. */
  matchedAddresses?: Set<string> | null;
  /** ADR 354 paint-by override for the carton tint. Null/undefined = the
   *  default status story (natural cartons, danger for unidentified). A new
   *  function identity recolors in place — StaticInstances rewrites instance
   *  colors, no remount (the ADR 199 rule). */
  boxPaint?: ((b: BoxPlacement) => string) | null;
}

/**
 * The goods themselves (ADR 191): one real PBR pallet + a stack of carton boxes
 * per occupied racked bin, replacing the extruded status prisms OccupancyLayer
 * used to draw. Scan state stays readable twice over — the deck tint under the
 * pallet keeps every status color, and unidentified stock (ADR 154) tints its
 * cartons UNIDENTIFIED_COLOR so the one class that sends a person to a bin
 * still shouts.
 *
 * Rendering is `StaticInstances` per ~24 m grid chunk (ADR 199): matrices
 * composed once, real instance bounds so the frustum culls whole blocks when
 * the camera is inside an aisle, and a per-chunk distance LOD that swaps the
 * GLB parts for 12-tri proxies in every chunk far enough that only
 * silhouettes read — near chunks alone carry the detailed parts.
 * The first version used drei's declarative `<Instances>` plus one invisible
 * pick mesh per occupied bin; at JSL scale (13k loads) that meant ~40k
 * scene-graph objects, 13k draws and a 2 FPS floor. The pick volumes are now
 * one instanced mesh per chunk resolved via `instanceId`, with the exact same
 * select semantics as the deck tint below. Loads do not cast shadows: the
 * scene's shadow camera never covered more than a corner patch, so their
 * shadow pass was pure vertex cost with no pixels to show for it.
 *
 * Product search (ADR 196): with a search active the loads split into two
 * instanced groups per part — matches keep the solid material and gain a
 * fresnel aura shell around the whole load (ADR 202); the rest re-render on a
 * cloned near-transparent material, so the found pallets read through the warehouse.
 * Per-instance opacity does not exist on instanced meshes; the two-group split
 * is the cheap, shader-free way to get per-bin transparency.
 */
export function PalletLoadLayer({ locations, matchedAddresses, boxPaint }: Props) {
  const palletGltf = useGLTF(PALLET_URL);
  const boxGltf = useGLTF(BOX_URL);
  const setSelectedBin = useUIStore((s) => s.setSelectedBin);
  const selectedBin = useUIStore((s) => s.selectedBin);
  const clearAssetSelection = useTelemetryStore((s) => s.clearSelection);

  const palletMesh = useMemo(() => firstMesh(palletGltf.scene), [palletGltf.scene]);
  const boxMesh = useMemo(() => firstMesh(boxGltf.scene), [boxGltf.scene]);
  const { pallets, boxes, picks } = useMemo(() => buildLoads(locations), [locations]);

  const searchActive = matchedAddresses != null;
  const { palletsSolid, palletsGhost, boxesSolid, boxesGhost } = useMemo(() => {
    if (!searchActive) return { palletsSolid: pallets, palletsGhost: [] as PalletPlacement[], boxesSolid: boxes, boxesGhost: [] as BoxPlacement[] };
    const matched = matchedAddresses;
    return {
      palletsSolid: pallets.filter((p) => matched.has(p.key)),
      palletsGhost: pallets.filter((p) => !matched.has(p.key)),
      boxesSolid: boxes.filter((b) => matched.has(boxAddress(b))),
      boxesGhost: boxes.filter((b) => !matched.has(boxAddress(b))),
    };
  }, [searchActive, matchedAddresses, pallets, boxes]);

  // Aura targets (ADR 202): every search match, plus the bin open in the
  // inspector. Memoized because their identity feeds StaticInstances' compose
  // effect and the mesh key.
  const matchedPicks = useMemo(
    () => (searchActive ? picks.filter((p) => matchedAddresses.has(p.cell.address)) : []),
    [searchActive, matchedAddresses, picks],
  );
  const selectedPick = useMemo(
    () => (selectedBin ? picks.find((p) => p.cell.address === selectedBin.address) ?? null : null),
    [picks, selectedBin],
  );

  // Pick volumes are search-independent — chunked on their own memo so a
  // keystroke never re-buckets them (ADR 212).
  const pickChunks = useMemo(() => chunkByGrid(picks), [picks]);
  // ADR 371: while a search is active only the matched loads answer the
  // pointer — the ghosts keep their see-through look but are dead to hover
  // and click. A predicate the pick volumes read at raycast time, so the
  // chunks above stay untouched by the search.
  const pickable = useMemo(
    () => (searchActive ? (p: PickVolume) => matchedAddresses.has(p.cell.address) : undefined),
    [searchActive, matchedAddresses],
  );
  // ADR 428: in the default status story an inferred back pallet's cartons are violet.
  // Tinting the cardboard material would come out a muddy brown (the ADR 393 lesson),
  // so they render on the neutral clone like a paint mode does. Under a paint mode they
  // stay with the rest: they carry the front pallet's product, dates included.
  const splitInferred = !boxPaint;
  const chunks = useMemo(() => ({
    palletsSolid: chunkByGrid(palletsSolid),
    palletsGhost: chunkByGrid(palletsGhost),
    boxesSolid: chunkByGrid(splitInferred ? boxesSolid.filter((b) => !b.inferred) : boxesSolid),
    boxesInferred: chunkByGrid(splitInferred ? boxesSolid.filter((b) => b.inferred) : []),
    boxesGhost: chunkByGrid(boxesGhost),
  }), [palletsSolid, palletsGhost, boxesSolid, boxesGhost, splitInferred]);

  // Per-chunk LOD — each chunk compares ITS center against the camera, so
  // zooming into an aisle details the racks in front of the camera while the
  // rest of the warehouse stays on proxies. `null` until the first sample =
  // everything far (the cheap default). Transitions carry per-chunk hysteresis
  // (lodFar), and state only updates when some chunk actually flips.
  const chunkKeys = useMemo(() => [...new Set([
    ...chunks.palletsSolid.keys(), ...chunks.palletsGhost.keys(),
    ...chunks.boxesSolid.keys(), ...chunks.boxesInferred.keys(), ...chunks.boxesGhost.keys(),
  ])], [chunks]);
  const [farKeys, setFarKeys] = useState<Set<string> | null>(null);
  const prevFarRef = useRef(new Map<string, boolean>());
  const frame = useRef(0);
  useFrame(({ camera, clock }) => {
    // Gentle beacon pulse on the search auras — one uniform write per frame,
    // shared by every matched load.
    if (matchedPicks.length > 0) {
      searchAuraMaterial.uniforms.uIntensity.value = 0.8 + 0.2 * Math.sin(clock.elapsedTime * 2.5);
    }
    if (++frame.current % LOD_CHECK_EVERY !== 0) return;
    const prev = prevFarRef.current;
    const next = new Set<string>();
    let changed = farKeys === null;
    for (const key of chunkKeys) {
      const c = chunkCenter(key);
      const d = Math.hypot(camera.position.x - c.x, camera.position.y - CHUNK_MID_Y, camera.position.z - c.y);
      const isFar = lodFar(d, prev.get(key) ?? true);
      if (isFar !== (prev.get(key) ?? true)) changed = true;
      prev.set(key, isFar);
      if (isFar) next.add(key);
    }
    if (changed) setFarKeys(next);
  });

  const palletGhostMat = useMemo(() => (palletMesh ? ghostOf(palletMesh.material) : null), [palletMesh]);
  const boxGhostMat = useMemo(() => (boxMesh ? ghostOf(boxMesh.material) : null), [boxMesh]);
  // ADR 393: a paint mode's tint must BE the legend's colour. The instance colour
  // multiplies the material's base, and the carton GLB's base is cardboard, so
  // yellow ("found, not in the WMS") came out the orange of "in the WMS, not
  // found". Painted cartons render on a neutral clone: no colour map, white base.
  const boxPaintMat = useMemo(() => (boxMesh ? neutralOf(boxMesh.material) : null), [boxMesh]);
  // ADR 279 hover shell's target — a hook, so it must sit above the early
  // return with the rest of them.
  const hoverRef = useRef<THREE.Mesh>(null);

  if (!palletMesh || !boxMesh || pallets.length === 0) return null;

  const isFarChunk = (key: string) => farKeys === null || farKeys.has(key);

  const palletChunks = (byKey: Map<string, PalletPlacement[]>, material: THREE.Material | THREE.Material[], tag: string) =>
    [...byKey].map(([key, items]) => (
      <StaticInstances key={`p${tag}:${key}:${items.length}`} items={items} compose={composePallet}
                       geometry={isFarChunk(key) ? PALLET_PROXY : palletMesh.geometry} material={material} cullable />
    ));

  const boxChunks = (byKey: Map<string, BoxPlacement[]>, material: THREE.Material | THREE.Material[], tag: string,
                     colorOf: (b: BoxPlacement) => string = boxPaint ?? boxColor) =>
    [...byKey].map(([key, items]) => (
      <StaticInstances key={`b${tag}:${key}:${items.length}`} items={items} compose={composeBox}
                       colorOf={colorOf} geometry={isFarChunk(key) ? BOX_PROXY : boxMesh.geometry} material={material} cullable />
    ));

  const pick = (items: PickVolume[]) => (e: ThreeEvent<MouseEvent>) => {
    e.stopPropagation();
    clearAssetSelection();
    setSelectedBin(items[e.instanceId ?? 0].cell);
    // The picked load switches to the steady selected shell — drop the hover
    // glow now instead of on the next pointer move.
    if (hoverRef.current && typeof hoverRef.current.position?.set === "function") {
      hoverRef.current.visible = false;
    }
  };

  // ADR 279: hover shell over the load column under the cursor — the selected
  // aura at a fraction of the volume, driven IMPERATIVELY (pointermove at
  // 60 Hz over 15k pick volumes must never set React state; same rule as the
  // deck ghost). The bin already open in the inspector keeps only its steady
  // shell — a second glow on it would read as a state change.
  const hoverLoad = (items: PickVolume[]) => (e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation();
    const g = hoverRef.current;
    if (!g || typeof g.position?.set !== "function") return; // jsdom renders the tag only
    const p = items[e.instanceId ?? 0];
    const isOpen = p && p.cell.address === useUIStore.getState().selectedBin?.address;
    if (!p || isOpen) { g.visible = false; return; }
    g.position.set(p.x, p.elev + p.h / 2, p.y);
    g.scale.set(p.w + AURA_PAD * 2, p.h + AURA_PAD * 2, p.d + AURA_PAD * 2);
    g.visible = true;
  };
  const hoverEnd = () => {
    const g = hoverRef.current;
    if (g && typeof g.position?.set === "function") g.visible = false;
    document.body.style.cursor = "auto";
  };

  return (
    <group position={[0, FLOOR_Y, 0]}>
      {palletChunks(chunks.palletsSolid, palletMesh.material, "s")}
      {searchActive && palletGhostMat && palletChunks(chunks.palletsGhost, palletGhostMat, "g")}
      {boxChunks(chunks.boxesSolid, boxPaint && boxPaintMat ? boxPaintMat : boxMesh.material, "s")}
      {boxPaintMat && boxChunks(chunks.boxesInferred, boxPaintMat, "i", inferredColor)}
      {searchActive && boxGhostMat && boxChunks(chunks.boxesGhost, boxGhostMat, "g")}
      {/* Aura around each matched load (ADR 202) — a pulsing fresnel rim-glow
          shell replacing the old translucent box: alpha lives on the
          silhouette, so the load reads *outlined in light* instead of boxed in
          glass. One instanced draw for every match (the boxes were one mesh
          each). CTA blue so it never collides with the status palette. */}
      {matchedPicks.length > 0 && (
        <StaticInstances key={`hl:${matchedPicks.length}`} items={matchedPicks}
                         compose={composeAura} geometry={AURA_GEOMETRY}
                         material={searchAuraMaterial} cullable />
      )}
      {/* The inspected bin wears the same shell in steady near-white — "this
          one is open in the inspector", distinct from the search matches. No
          event props, so it never shadows the pick volumes' raycasts. */}
      {selectedPick && (
        <mesh
          position={[selectedPick.x, selectedPick.elev + selectedPick.h / 2, selectedPick.y]}
          scale={[selectedPick.w + AURA_PAD * 2, selectedPick.h + AURA_PAD * 2, selectedPick.d + AURA_PAD * 2]}
          geometry={AURA_GEOMETRY}
          material={selectedAuraMaterial}
        />
      )}
      {/* Invisible pick volumes — the whole load column stays clickable (the
          ghosted ones excepted, ADR 371). One instanced mesh per chunk;
          colorWrite off so the GPU never shades them, and the chunk bounds
          pre-cull raycasts too. */}
      {[...pickChunks].map(([key, items]) => (
        <StaticInstances
          key={`pk:${key}:${items.length}`}
          items={items}
          compose={composePick}
          cullable
          interactive={pickable}
          onClick={pick(items)}
          onPointerOver={(e) => { e.stopPropagation(); document.body.style.cursor = "pointer"; }}
          onPointerMove={hoverLoad(items)}
          onPointerOut={hoverEnd}
        >
          <boxGeometry args={[1, 1, 1]} />
          <meshBasicMaterial transparent opacity={0} colorWrite={false} depthWrite={false} />
        </StaticInstances>
      ))}
      {/* ADR 279: the hover shell — no event props, so it never shadows the
          pick volumes' raycasts (same rule as the selected shell above). */}
      <mesh
        ref={hoverRef}
        visible={false}
        geometry={AURA_GEOMETRY}
        material={hoverAuraMaterial}
        raycast={() => null}
      />
    </group>
  );
}
